import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, ChevronDown } from 'lucide-react';
import { useAppAccess, APP_LABELS } from '../context/AppAccessContext';
import './AppSwitcher.css';

// Until Inventory/Sales are carved into their own routed sections, switching
// just remembers the choice and lands on the closest existing entry point.
const APP_LANDING_ROUTE: Record<string, string> = {
  platform: '/settings/company',
  inventory: '/dashboard',
  sales: '/dashboard',
  restaurant: '/restaurant/orders',
  carRental: '/car-rental/schedule',
  automobile: '/automobile/vehicles',
  accounting: '/accounting/chart-of-accounts',
};

const AppSwitcher: React.FC = () => {
  const { availableApps, currentApp, switchApp } = useAppAccess();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  if (availableApps.length <= 1) return null;

  const handleSelect = (appCode: string) => {
    switchApp(appCode);
    setOpen(false);
    navigate(APP_LANDING_ROUTE[appCode] ?? '/dashboard');
  };

  return (
    <div className="app-switcher" ref={ref}>
      <button
        className="app-switcher__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label="Switch application"
        title="Switch application"
      >
        <LayoutGrid size={16} strokeWidth={2} aria-hidden="true" />
        <span className="app-switcher__current">
          {currentApp ? APP_LABELS[currentApp] ?? currentApp : 'Select app'}
        </span>
        <ChevronDown size={14} strokeWidth={2} aria-hidden="true" />
      </button>

      {open && (
        <div className="app-switcher__menu" role="menu">
          {availableApps.map((appCode) => (
            <button
              key={appCode}
              className={`app-switcher__item ${appCode === currentApp ? 'app-switcher__item--active' : ''}`}
              onClick={() => handleSelect(appCode)}
              role="menuitem"
            >
              {APP_LABELS[appCode] ?? appCode}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppSwitcher;
